import { BaseClient } from '../BaseClient';
import { type String$ } from '../compiled-protos/common';
import {
  AiChatPrompt,
  AiChatPromptList,
  AiResponse,
  AiResponseList,
  type AiResponseReport,
  type AiResponseReportData,
  type AiResponseReportFile,
  type AiResponseReportFileList,
  type AiResponseReportFileWithUrl,
  type AiResponseReportList,
  type AiResponseSystem,
  type AiResponseSystemList,
  type Assistant,
  type AssistantList,
  type AssistantThread,
  type BatchUpdateAiResponseReportFilesRequest,
  type OpenAiFile,
  type OpenAiFileList,
  type StartThreadResponse,
  type StreamMessageRequest,
  ToolResources,
  type UploadOpenAiFile,
  type VectorStore,
  type VectorStoreFile,
  type VectorStoreFileList,
  type VectorStoreFilesRequest,
  type VectorStoreList,
  type VectorStoreRequest,
} from '../compiled-protos/openai';
import { OpenAiServiceClient } from '../compiled-protos/openai.client';
import { type FileObjects } from '../compiled-protos/s3';

export class OpenAIClient extends BaseClient {
  self: OpenAiServiceClient;

  constructor(host: string, userID?: number) {
    super(host, userID);
    this.self = new OpenAiServiceClient(this.transport);
  }
  
  /**
   * CreateAssistant creates a new assistant with the given parameters.
   * We need to impliment this method with the correct request type.
   * @param req The assistant to create.
   */
  public async CreateAssistant(req: Assistant) {
    let res: Assistant
    try {
      res = await this.self.createOpenAiAssistant(req, this.getMetaData())
        .response;
    } catch (err) {
      console.error('Error creating assistant:', err);
      return;
    }
    return res;
  }
  

  public async ListAssistants() {
    let res: AssistantList
    try {
      res = await this.self.listAssistants({}, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  /**
   * UpdateAssistant modifies an existing assistant with the given parameters.
   * We need to impliment this methos with the correct request type.
   * @param req The assistant to modify.
   */
  public async UpdateAssistant(req: Assistant) {
    let res: Assistant
    try {
      res = await this.self.updateOpenAiAssistant(req, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  /**
   * DeleteAssistant deletes an assistant with the given id.
   * @param id 
   * @returns 
   */
  public async DeleteAssistant(id: string) {
    const req: String$ = { value: id }
    try {
      await this.self.deleteOpenAiAssistant(req, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return;
    }
    return true
  }

  public async StartThread(asistantId: string, toolResources?: ToolResources) {
    let res: StartThreadResponse
    try {
      res = await this.self.startThread({
        assistantId: asistantId,
        toolResources: toolResources
      }, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async CreateAssistantThread(assistantId: string, userID: number, threadId: string) {
    let res: AssistantThread
    try {
      res = await this.self.createAssistantThread({
        assistantId: assistantId,
        userId: userID,
        isActive: true,
        threadId: threadId,
        fieldMask: [],
        dateCreated: new Date().toISOString(),
        id: 0,
        runId: "",
        status: "",
        response: "",
        serviceType: "",
        lastUpdate: new Date().toISOString(),
        statusList: ""
      }, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async UpdateAssistantThread(thread: AssistantThread) {
    try {
      await this.self.updateAssistantThread(thread, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return true
    }
    return false
  } 

  public async DeleteAssistantThread(thread: AssistantThread) {
    try {
      await this.self.deleteAssistantThread(thread, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return true
    }
    return false
  }

  public async GetAssistantThread(thread: AssistantThread) {
    let res: AssistantThread
    try {
      res = await this.self.getAssistantThread(thread, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  /** Usage of this function is unique comapred to other methods in the API
   * 
   * This function is used to create a stream of messages between the user and the assistant
   * Therefore, it returns an awaitable stream of messages which must be iterated through by
   * the consumer.
   * 
   * Example:
   * 
   * const stream = openAIclient.AssistantThreadChat(req);
   *      let isFirstMessage = true;
   *       for await (const msg of stream) {
   *      if (isFirstMessage) {
  *      appendMessageDisplay('ai', msg.message);
  *        isFirstMessage = false;
  *         } else {
  *           updateLastMessageDisplay(msg.message);
  *         }
  *
  **
  */
  public async *AssistantThreadChat(req: StreamMessageRequest) {
    try {
      const call = this.self.assistantThreadChat(req, this.getMetaData());
      for await (const msg of call.responses) {
        yield msg;
      }
    } catch (err) {
      console.log(err);
      return;
    }
  }
  
  public async UploadFile(file: UploadOpenAiFile) {
    let res: OpenAiFile
    try {
      res = await this.self.uploadFile(file, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async ListFiles() {
    let res: OpenAiFileList
    try {
      res = await this.self.listFiles({}, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async DeleteFile(fileId: string) {
    const req: String$ = { value: fileId }
    try {
      await this.self.deleteFile(req, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return false;
    }
    return true
  }

  public async CreateVectorStore(req: VectorStoreRequest) {
    let res: VectorStore
    try {
      res = await this.self.createVectorStore(req, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async ListVectorStores() {
    let res: VectorStoreList
    try {
      res = await this.self.listVectorStores({}, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  /**
   *CreateVectorStoreFiles adds files to an existing vector store.
   * 
   * @param req VectorStoreFilesRequest
   */
  public async CreateVectorStoreFiles(req: VectorStoreFilesRequest) {
    let res: any
    try {
     res = await this.self.createVectorStoreFiles(req, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }
    

  public async ListVectorStoreFiles(store: VectorStore) {
    let res: VectorStoreFileList
    try {
      res = await this.self.listVectorStoreFiles(store, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async DeleteVectorStoreFile(file: VectorStoreFile) {
    try {
      await this.self.deleteVectorStoreFile(file, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return false;
    }
    return true
  }

  public async DeleteThread(asistantId: string) {
    const req: String$ = { value: asistantId }
    try {
      await this.self.deleteThread(req, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return false;
    }
    return true
  }

  public async MakeChatCompletionRequest(query: string) {
    const req: String$ = { value: query }
    let res: String$
    try {
      res = await this.self.makeChatCompletionRequest(req, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  /**
   * UploadS3FileList uploads a list of files from S3 to the OpenAI API.
   * 
   * @param input FileObjects
   */
  public async UploadS3FileList(input: FileObjects) {
    let res: OpenAiFileList
    try {
      res = await this.self.uploadS3FileList(input, this.getMetaData())
        .response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  /**
   * CreateAiResponse creates a new AI response record.
   * @param req The AI response to create.
   */
  public async CreateAiResponse(req: AiResponse) {
    let res: AiResponse;
    try {
      res = await this.self.createAiResponse(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error creating AI response:', err);
      return;
    }
    return res;
  }

  /**
   * UpdateAiResponse updates an existing AI response record.
   * @param req The AI response to update.
   */
  public async UpdateAiResponse(req: AiResponse) {
    let res: AiResponse;
    try {
      res = await this.self.updateAiResponse(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error updating AI response:', err);
      return;
    }
    return res;
  }

  /**
   * DeleteAiResponse deletes an AI response record.
   * @param req The AI response to delete.
   */
  public async DeleteAiResponse(req: AiResponse) {
    try {
      await this.self.deleteAiResponse(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error deleting AI response:', err);
      return false;
    }
    return true;
  }

  /**
   * GetAiResponse retrieves a single AI response record.
   * @param req The AI response filter.
   */
  public async GetAiResponse(req: AiResponse) {
    let res: AiResponse;
    try {
      res = await this.self.getAiResponse(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error getting AI response:', err);
      return;
    }
    return res;
  }

  /**
   * BatchGetAiResponse retrieves multiple AI response records.
   * @param req The AI response filter.
   */
  public async BatchGetAiResponse(req: AiResponse) {
    let res: AiResponseList;
    try {
      res = await this.self.batchGetAiResponse(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error batch getting AI responses:', err);
      return;
    }
    return res;
  }

  /**
   * GetAiChatPrompt retrieves a single AI chat prompt record.
   * @param req The AI chat prompt filter.
   */
  public async GetAiChatPrompt(req: AiChatPrompt) {
    let res: AiChatPrompt;
    try {
      res = await this.self.getAiChatPrompt(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error getting AI chat prompt:', err);
      return;
    }
    return res;
  }

  /**
   * BatchGetAiChatPrompt retrieves multiple AI chat prompt records.
   * @param req The AI chat prompt filter.
   */
  public async BatchGetAiChatPrompt(req: AiChatPrompt) {
    let res: AiChatPromptList;
    try {
      res = await this.self.batchGetAiChatPrompt(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error batch getting AI chat prompts:', err);
      return;
    }
    return res;
  }

  /**
   * GetAiResponseReport retrieves a single AI response report record.
   */
  public async GetAiResponseReport(req: AiResponseReport) {
    let res: AiResponseReport | undefined;
    try {
      res = await this.self.getAiResponseReport(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error getting AI response report:', err);
      return;
    }
    return res;
  }

  /**
   * BatchGetAiResponseReport retrieves AI response reports by filter (e.g. ai_responses_id).
   */
  public async BatchGetAiResponseReport(req: AiResponseReport) {
    let res: AiResponseReportList | undefined;
    try {
      res = await this.self.batchGetAiResponseReport(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error batch getting AI response reports:', err);
      return;
    }
    return res;
  }

  /**
   * BatchGetAiResponsesSystem retrieves ai_responses_system records by filter (e.g. ai_responses_id).
   */
  public async BatchGetAiResponsesSystem(req: AiResponseSystem) {
    let res: AiResponseSystemList | undefined;
    try {
      res = await this.self.batchGetAiResponsesSystem(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error batch getting AI responses system:', err);
      return;
    }
    return res;
  }

  /**
   * CreateAiResponseReport creates a new AI response report.
   */
  public async CreateAiResponseReport(req: AiResponseReport) {
    let res: AiResponseReport | undefined;
    try {
      res = await this.self.createAiResponseReport(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error creating AI response report:', err);
      return;
    }
    return res;
  }

  /**
   * CreateAiResponseReportFile creates a report file link.
   */
  public async CreateAiResponseReportFile(req: AiResponseReportFile) {
    let res: AiResponseReportFile | undefined;
    try {
      res = await this.self.createAiResponseReportFile(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error creating AI response report file:', err);
      return;
    }
    return res;
  }

  /**
   * GetAiResponseReportData retrieves report with files and expiring URLs.
   */
  public async GetAiResponseReportData(req: AiResponseReport) {
    let res: AiResponseReportData | undefined;
    try {
      res = await this.self.getAiResponseReportData(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error getting AI response report data:', err);
      return;
    }
    return res;
  }

  /**
   * UpdateAiResponseReport updates an AI response report record.
   */
  public async UpdateAiResponseReport(req: AiResponseReport) {
    let res: AiResponseReport | undefined;
    try {
      res = await this.self.updateAiResponseReport(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error updating AI response report:', err);
      return;
    }
    return res;
  }

  /**
   * BatchGetAiResponseReportFile retrieves report files.
   */
  public async BatchGetAiResponseReportFile(req: AiResponseReportFile) {
    let res: AiResponseReportFileList | undefined;
    try {
      res = await this.self.batchGetAiResponseReportFile(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error batch getting AI response report files:', err);
      return;
    }
    return res;
  }

  /**
   * UpdateAiResponseReportFile updates a report file (is_selected, display_order).
   */
  public async UpdateAiResponseReportFile(req: AiResponseReportFile) {
    let res: AiResponseReportFile | undefined;
    try {
      res = await this.self.updateAiResponseReportFile(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error updating AI response report file:', err);
      return;
    }
    return res;
  }

  /**
   * BatchUpdateAiResponseReportFiles updates multiple report files in one request.
   */
  public async BatchUpdateAiResponseReportFiles(req: BatchUpdateAiResponseReportFilesRequest) {
    let res: AiResponseReportFileList | undefined;
    try {
      res = await this.self.batchUpdateAiResponseReportFiles(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error batch updating AI response report files:', err);
      return;
    }
    return res;
  }

  /**
   * DownloadAiResponseReport generates PDF and returns presigned URL.
   */
  public async DownloadAiResponseReport(req: AiResponseReport) {
    try {
      const urlObj = await this.self.downloadAiResponseReport(req, this.getMetaData()).response;
      return urlObj;
    } catch (err) {
      console.error('Error downloading AI response report:', err);
      return;
    }
  }
}

export {
  AiResponse,
  AiResponseList,
  AiChatPrompt,
  AiChatPromptList,
  type AiResponseReport,
  type AiResponseReportData,
  type AiResponseReportFile,
  type AiResponseReportFileList,
  type AiResponseReportList,
  type AiResponseReportFileWithUrl,
  type BatchUpdateAiResponseReportFilesRequest,
  type AssistantList,
  type AssistantThread,
  type OpenAiFile,
  type OpenAiFileList,
  type StartThreadResponse,
  type StreamMessageRequest,
  ToolResources,
  type UploadOpenAiFile,
  type VectorStore,
  type VectorStoreFile,
  type VectorStoreFileList,
  type VectorStoreList,
  type VectorStoreRequest,
}